/**
 * DraftsPage — triage cross-œuvres des drafts générés Claude (Sprint 3).
 *
 * Pourquoi cette page existe : à 50+ œuvres × 6 plateformes = 300 drafts
 * pending, naviguer œuvre par œuvre dans `WorkDetailPage` devient
 * ingérable. La page `/drafts` offre une vue de triage avec filtres URL,
 * KPIs Tier 4 conditionnels, et pagination "Charger plus".
 *
 * Réutilisation maximale :
 * - `<DraftCard>` (mêmes callbacks approve/regenerate/reject)
 * - `<SearchInput>`, `<Select>`, `<FilterChips>` (primitives DS)
 * - Mutations cross-œuvres (workId pas connu d'avance) → invalident
 *   `draftsKeys.all` pour synchroniser le badge sidebar en temps réel.
 *
 * UX clé :
 * - Reset offset=0 sur tout changement de filtre/sort/q (sinon une bascule
 *   `pending → approved` à offset=200 retourne une page vide).
 * - EmptyState contextualisé selon `status` filtré.
 * - KPIs Tier 4 cachés sous `MIN_DRAFTS_FOR_TIER4_KPI = 10` par plateforme
 *   (statistiquement trompeurs en dessous).
 */

import { useMemo, useState } from 'react'
import { Link } from 'react-router'
import { CheckCircle2, FileText } from 'lucide-react'
import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import EmptyState from '@/components/ui/EmptyState'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import FilterChips, { type FilterChip } from '@/components/ui/FilterChips'
import KPI from '@/components/ui/KPI'
import SearchInput from '@/components/ui/SearchInput'
import Select from '@/components/ui/Select'
import MockupTemplatePicker from '@/components/etsy/MockupTemplatePicker'
import DraftApproveDialog from '@/components/drafts/DraftApproveDialog'
import DraftCard from '@/components/drafts/DraftCard'
import DraftPreviewModal from '@/components/drafts/DraftPreviewModal'
import MissingDraftsBanner from '@/components/drafts/MissingDraftsBanner'
import Page from '@/components/layout/Page'
import PageToolbar from '@/components/layout/PageToolbar'
import { useConfirm } from '@/contexts/confirm-dialog-context'
import useListFilters from '@/hooks/useListFilters'
import {
  useAllDrafts,
  useApproveDraftCross,
  useDraftsPendingCount,
  useDraftsStats,
  useRegenerateDraftCross,
  useRejectDraftCross,
  useReopenDraftCross,
} from '@/hooks/useDrafts'
import { workPublicationsKeys } from '@/hooks/useWorkPublications'
import { useWork, worksKeys } from '@/hooks/useWorks'
import { worksApi } from '@/utils/api/works'
import type { components } from '@/types/api-generated'

type WorkVariantWithCatalogResponse = components['schemas']['WorkVariantWithCatalogResponse']
import { workPodPhysicalApi } from '@/utils/api/work-pod-physical'
import { workPublicationsApi } from '@/utils/api/work-publications'
import type { DraftMockupPreview } from '@/utils/api/drafts'
import { draftListFiltersSchema } from '@/schemas/drafts'
import {
  DRAFT_KIND_FR,
  DRAFT_KIND_OPTIONS_GROUPED,
  DRAFT_KIND_TO_PLATFORM,
  DRAFT_LIST_SORT_FR,
  DRAFT_LIST_SORT_OPTIONS,
  DRAFT_STATUS_FR,
  DRAFT_STATUS_OPTIONS,
  MIN_DRAFTS_FOR_TIER4_KPI,
  PLATFORM_FR,
  PLATFORM_OPTIONS,
  type DraftListSort,
  type DraftResponse,
} from '@/types/drafts'

import './DraftsPage.css'

const PAGE_SIZE = 50

// Filtre par medium_kind cohérent avec <DraftsTab> (commit 5c12618). Hoist
// au scope module — pas de dépendance closure, et appel depuis useMemo
// AVANT la déclaration locale serait TDZ (cf. bug 2026-05-15 sur prod).
function filterCompatibleVariants(
  variants: WorkVariantWithCatalogResponse[],
  draftKind: DraftResponse['kind'],
): WorkVariantWithCatalogResponse[] {
  const active = variants.filter((v) => v.status === 'active')
  if (draftKind === 'etsy_listing_physical') {
    return active.filter((v) => v.medium_kind === 'print' || v.medium_kind === 'original')
  }
  if (draftKind === 'etsy_listing_digital') {
    return active.filter((v) => v.medium_kind === 'digital')
  }
  return active
}

export default function DraftsPage() {
  const { filters, setFilter, setFilters, removeFilter, clear, activeKeys } =
    useListFilters(draftListFiltersSchema)

  const offset = filters.offset ?? 0
  const sort: DraftListSort = filters.sort ?? 'recent'

  // Buffer local SearchInput — l'URL n'est mise à jour qu'après debounce
  // 300ms côté SearchInput. Pattern aligné avec WorksPage.
  const [searchDraft, setSearchDraft] = useState(filters.q ?? '')
  const [prevFilterQ, setPrevFilterQ] = useState(filters.q ?? '')
  if (prevFilterQ !== (filters.q ?? '')) {
    setPrevFilterQ(filters.q ?? '')
    setSearchDraft(filters.q ?? '')
  }

  const draftsQuery = useAllDrafts({
    status: filters.status,
    platform: filters.platform,
    kind: filters.kind,
    q: filters.q,
    sort,
    limit: PAGE_SIZE + offset, // "Charger plus" cumule, pas de pages numérotées
    offset: 0,
  })
  const statsQuery = useDraftsStats()

  const approveMutation = useApproveDraftCross()
  const regenerateMutation = useRegenerateDraftCross()
  const rejectMutation = useRejectDraftCross()
  const reopenMutation = useReopenDraftCross()
  const { confirm } = useConfirm()

  // PR2 — Flow approve cross-works (3 écrans : prep → mockups → preview).
  const [flowState, setFlowState] = useState<
    | { step: 'prep'; draft: DraftResponse }
    | {
        step: 'mockups'
        draft: DraftResponse
        workVariantId: number
        acceptedTags: string[]
      }
    | {
        step: 'preview'
        draft: DraftResponse
        workVariantId: number
        previews: DraftMockupPreview[]
      }
    | null
  >(null)
  // L'œuvre du draft en flow — chargée à la demande pour le picker.
  const flowWorkQuery = useWork(flowState?.draft.work_id ?? null)
  // Bug 2026-05-15 — filtre par medium_kind cohérent avec <DraftsTab>
  // (commit 5c12618). La page /drafts a son propre flow car <DraftsTab>
  // vit dans la fiche œuvre (work déjà chargé) tandis que /drafts charge
  // l'œuvre lazy via flowWorkQuery. Helper `filterCompatibleVariants`
  // partagé avec handleApprove pour cohérence pré-ouverture / rendu dialog.
  const flowCompatibleVariants = useMemo(() => {
    if (flowState?.step !== 'prep') return []
    return filterCompatibleVariants(flowWorkQuery.data?.variants ?? [], flowState.draft.kind)
  }, [flowWorkQuery.data, flowState])

  // Mutation publish ad-hoc — `platform` dépend du draft cliqué.
  const qc = useQueryClient()
  const enqueuePublishMutation = useMutation({
    mutationFn: ({
      workId,
      platform,
      payload,
    }: {
      workId: number
      platform: Parameters<typeof workPublicationsApi.enqueuePublish>[1]
      payload: Parameters<typeof workPublicationsApi.enqueuePublish>[2]
    }) => workPublicationsApi.enqueuePublish(workId, platform, payload),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: workPublicationsKeys.forWork(vars.workId, vars.platform),
      })
    },
  })

  const data = draftsQuery.data
  const items = data?.items ?? []
  const total = data?.total ?? 0
  const hasMore = items.length < total

  // Reset offset à 0 sur tout changement de filtre/sort/q. On le fait dans
  // les setters dédiés ci-dessous plutôt que via un effet, pour éviter le
  // double-render et garder l'URL canonique stable.
  const setFilterAndResetOffset = <K extends keyof typeof filters>(
    key: K,
    value: (typeof filters)[K] | undefined,
  ) => {
    if (key === 'offset') {
      setFilter(key, value)
      return
    }
    setFilters({ [key]: value, offset: undefined } as Partial<typeof filters>)
  }

  // ── Chips actifs ────────────────────────────────────────────────────────
  const chips: FilterChip[] = useMemo(() => {
    const list: FilterChip[] = []
    if (filters.q) list.push({ key: 'q', label: `Recherche : « ${filters.q} »` })
    if (filters.status)
      list.push({ key: 'status', label: `Statut : ${DRAFT_STATUS_FR[filters.status]}` })
    if (filters.platform)
      list.push({ key: 'platform', label: `Plateforme : ${PLATFORM_FR[filters.platform]}` })
    if (filters.kind) list.push({ key: 'kind', label: `Variante : ${DRAFT_KIND_FR[filters.kind]}` })
    return list
  }, [filters.q, filters.status, filters.platform, filters.kind])

  const handleRemoveChip = (key: string) => {
    if (key === 'q' || key === 'status' || key === 'platform' || key === 'kind') {
      // removeFilter ne touche pas à offset. On retire la clé + reset offset.
      removeFilter(key)
      if (offset > 0) removeFilter('offset')
    }
  }

  // ── KPIs Tier 4 ─────────────────────────────────────────────────────────
  // Cachés sous le seuil de 10 drafts par plateforme (sinon edit_rate /
  // avg_edit_distance sont statistiquement trompeurs). Si aucune plateforme
  // n'atteint le seuil, le bandeau entier disparaît.
  const visibleStats = useMemo(() => {
    const all = statsQuery.data?.by_platform ?? []
    return all.filter((s) => s.total >= MIN_DRAFTS_FOR_TIER4_KPI)
  }, [statsQuery.data])

  const showKpis = visibleStats.length > 0

  // ── Compteurs globaux pour subtitle (dérivés de la requête courante :
  // `total` reflète les filtres mais on veut garder un subtitle stable
  // cross-filtres. Le `pending_count` global a migré (S5c) vers un endpoint
  // léger dédié — pas mélangé à la page filtrée).
  const pendingCountQuery = useDraftsPendingCount()
  const pendingCount = pendingCountQuery.data?.count ?? 0

  // ── Mutations — flow approve PR2 ─────────────────────────────────────
  const handleApprove = async (draft: DraftResponse) => {
    // Pré-fetch l'œuvre pour valider qu'au moins 1 variante compatible
    // existe AVANT d'ouvrir le dialog (sinon dialog s'ouvrirait avec une
    // liste vide post-load → bouton désactivé sans message clair).
    // `fetchQuery` réutilise le cache TanStack Query si l'œuvre est déjà
    // chargée — pas de double round-trip.
    const work = await qc.fetchQuery({
      queryKey: worksKeys.detail(draft.work_id),
      queryFn: () => worksApi.get(draft.work_id),
    })
    const allVariants = work.variants ?? []
    const activeVariants = allVariants.filter((v) => v.status === 'active')
    if (activeVariants.length === 0) {
      toast.error("Aucune variante active sur l'œuvre — crée une variante avant d'approuver.")
      return
    }
    const compatible = filterCompatibleVariants(allVariants, draft.kind)
    if (compatible.length === 0) {
      const needed =
        draft.kind === 'etsy_listing_physical'
          ? 'print ou original'
          : draft.kind === 'etsy_listing_digital'
            ? 'digital'
            : 'compatible'
      toast.error(
        `Aucune variante ${needed} active sur cette œuvre — active une variante ${needed} avant d'approuver ce draft (ou approuve un autre kind).`,
      )
      return
    }
    setFlowState({ step: 'prep', draft })
  }

  const handlePrepConfirm = (selection: { workVariantId: number; acceptedTags: string[] }) => {
    if (flowState?.step !== 'prep') return
    setFlowState({
      step: 'mockups',
      draft: flowState.draft,
      workVariantId: selection.workVariantId,
      acceptedTags: selection.acceptedTags,
    })
  }

  const handleMockupsConfirm = async (
    mockupTemplateIds: number[],
    pinterestTemplateIds: number[],
  ) => {
    if (flowState?.step !== 'mockups') return
    const { draft, workVariantId, acceptedTags } = flowState
    try {
      const result = await approveMutation.mutateAsync({
        draftId: draft.id,
        payload: {
          mockup_template_ids: mockupTemplateIds,
          work_variant_id: workVariantId,
          accept_suggested_tags: acceptedTags.length > 0 ? acceptedTags : null,
          pinterest_template_ids: pinterestTemplateIds.length > 0 ? pinterestTemplateIds : null,
        },
      })
      setFlowState({
        step: 'preview',
        draft,
        workVariantId,
        previews: result.previews,
      })
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Approbation impossible : ${detail}`)
      setFlowState(null)
    }
  }

  const handleConfirmPublish = async (orderedTemplateIds: number[]) => {
    if (flowState?.step !== 'preview') return
    const { draft, workVariantId } = flowState
    const platform = DRAFT_KIND_TO_PLATFORM[draft.kind]
    try {
      // Phase 8 — refonte M : si l'œuvre a une matrice POD active ET le
      // draft est etsy_listing_physical, route vers publish_matrix (1
      // listing Etsy matriciel multi-variant). Sinon fallback ancien
      // flow single-variant. La page /drafts étant multi-work, on lit
      // la matrice à la demande (pas via hook).
      let routedToMatrix = false
      if (platform === 'etsy' && draft.kind === 'etsy_listing_physical') {
        const matrix = await workPodPhysicalApi.getForWork(draft.work_id)
        if (matrix !== null) {
          await workPodPhysicalApi.publish(matrix.id, {
            draft_id: draft.id,
            mockup_template_ids: orderedTemplateIds,
          })
          routedToMatrix = true
        }
      }
      if (!routedToMatrix) {
        await enqueuePublishMutation.mutateAsync({
          workId: draft.work_id,
          platform,
          payload: {
            variant_id: workVariantId,
            draft_id: draft.id,
            mockup_template_ids: orderedTemplateIds,
          },
        })
      }
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} approuvé et mis en file.`)
      setFlowState(null)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Mise en file impossible : ${detail}`)
    }
  }

  const handleRegenerate = async (draft: DraftResponse, hint: string | null) => {
    try {
      await regenerateMutation.mutateAsync({
        draftId: draft.id,
        payload: { extra_hint: hint },
      })
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} régénéré.`)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Régénération impossible : ${detail}`)
    }
  }

  const handleReject = async (draft: DraftResponse) => {
    const ok = await confirm({
      title: `Rejeter ce draft ${DRAFT_KIND_FR[draft.kind]} ?`,
      message:
        'Le draft sera marqué rejeté. Il alimentera la liste de patterns à éviter pour les futures générations.',
      variant: 'danger',
      confirmText: 'Rejeter',
    })
    if (!ok) return
    try {
      await rejectMutation.mutateAsync(draft.id)
      toast.success('Draft rejeté.')
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Rejet impossible : ${detail}`)
    }
  }

  const handleReopen = async (draft: DraftResponse) => {
    try {
      await reopenMutation.mutateAsync(draft.id)
      toast.success(`Draft ${DRAFT_KIND_FR[draft.kind]} réouvert.`)
    } catch (err) {
      const detail = (err as { detail?: string }).detail ?? 'Erreur inconnue'
      toast.error(`Réouverture impossible : ${detail}`)
    }
  }

  // Quel draft est en cours de mutation ? On bloque l'interaction sur lui.
  const busyDraftId = (() => {
    if (approveMutation.isPending) return approveMutation.variables?.draftId
    if (regenerateMutation.isPending) return regenerateMutation.variables?.draftId
    if (rejectMutation.isPending) return rejectMutation.variables
    if (reopenMutation.isPending) return reopenMutation.variables
    return undefined
  })()

  // ── EmptyState contextualisé ────────────────────────────────────────────
  const emptyMessage = (() => {
    if (filters.status === 'pending') {
      return {
        title: 'Tous les drafts sont validés',
        description:
          'Aucun draft en attente avec ces filtres. Tu peux générer de nouveaux drafts depuis une œuvre.',
      }
    }
    if (filters.status === 'approved') {
      return {
        title: 'Aucun draft approuvé',
        description: 'Commence par valider un draft sur une œuvre.',
      }
    }
    if (filters.status === 'rejected') {
      return {
        title: 'Aucun draft rejeté',
        description: "C'est plutôt un bon signe.",
      }
    }
    if (activeKeys.length > 0) {
      return {
        title: 'Aucun résultat',
        description: 'Aucun draft ne correspond aux filtres actifs.',
      }
    }
    return {
      title: 'Aucun draft généré',
      description: 'Crée une œuvre et lance la génération depuis son onglet Contenu généré.',
    }
  })()

  const isInitialLoading = draftsQuery.isLoading && items.length === 0

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Page width="wide">
      <Page.Header
        eyebrow="Atelier"
        title="Contenus"
        subtitle={
          isInitialLoading
            ? 'Chargement…'
            : `${pendingCount} en attente · ${total} résultat${total > 1 ? 's' : ''} affiché${total > 1 ? 's' : ''} avec les filtres actifs.`
        }
      />

      <MissingDraftsBanner />

      {showKpis && (
        <section className="drafts-page-kpis" aria-label="Statistiques par plateforme">
          {visibleStats.map((s) => (
            <KPI
              key={s.platform}
              label={PLATFORM_FR[s.platform]}
              value={s.total}
              legend={
                <>
                  {s.approved} approuvés · taux d'édition {Math.round((s.edit_rate ?? 0) * 100)}%
                </>
              }
            />
          ))}
        </section>
      )}

      <PageToolbar
        search={
          <SearchInput
            value={searchDraft}
            onChange={setSearchDraft}
            onSearch={(v) => setFilterAndResetOffset('q', v || undefined)}
            placeholder="Rechercher dans les titres d'œuvres…"
            ariaLabel="Recherche dans les drafts"
          />
        }
        onRefresh={() => void draftsQuery.refetch()}
        refreshDisabled={draftsQuery.isFetching}
      >
        <div className="drafts-page-filters-row">
          <Select
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilterAndResetOffset(
                'status',
                e.target.value ? (e.target.value as typeof filters.status) : undefined,
              )
            }
            aria-label="Filtrer par statut"
          >
            <option value="">Tous statuts</option>
            {DRAFT_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {DRAFT_STATUS_FR[s]}
              </option>
            ))}
          </Select>
          <Select
            value={filters.platform ?? ''}
            onChange={(e) =>
              setFilterAndResetOffset(
                'platform',
                e.target.value ? (e.target.value as typeof filters.platform) : undefined,
              )
            }
            aria-label="Filtrer par plateforme"
          >
            <option value="">Toutes plateformes</option>
            {PLATFORM_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {PLATFORM_FR[p]}
              </option>
            ))}
          </Select>
          <Select
            value={filters.kind ?? ''}
            onChange={(e) =>
              setFilterAndResetOffset(
                'kind',
                e.target.value ? (e.target.value as typeof filters.kind) : undefined,
              )
            }
            aria-label="Filtrer par variante"
          >
            <option value="">Toutes variantes</option>
            {/* Refonte drafts v12 — kinds groupés par plateforme via
                <optgroup>. Permet aux 2 sous-kinds Etsy (PHYSICAL +
                DIGITAL) d'apparaître sous le même header "Etsy" plutôt
                que noyés dans la liste plate. */}
            {DRAFT_KIND_OPTIONS_GROUPED.map((group) => (
              <optgroup key={group.platform} label={PLATFORM_FR[group.platform]}>
                {group.kinds.map((k) => (
                  <option key={k} value={k}>
                    {DRAFT_KIND_FR[k]}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <Select
            value={sort}
            onChange={(e) => setFilterAndResetOffset('sort', e.target.value as DraftListSort)}
            aria-label="Trier"
          >
            {DRAFT_LIST_SORT_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {DRAFT_LIST_SORT_FR[s]}
              </option>
            ))}
          </Select>
        </div>
        {chips.length > 0 && (
          <FilterChips chips={chips} onRemove={handleRemoveChip} onClearAll={clear} />
        )}
      </PageToolbar>

      {isInitialLoading ? (
        <p className="ui-text-muted">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={activeKeys.length > 0 ? FileText : CheckCircle2}
          title={emptyMessage.title}
          description={emptyMessage.description}
          action={
            activeKeys.length > 0 ? (
              <Button variant="ghost" onClick={clear}>
                Effacer les filtres
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="drafts-page-grid">
            {items.map((draft) => (
              <article key={draft.id} className="drafts-page-card-wrapper">
                {draft.work_title_fr && (
                  <header className="drafts-page-card-header">
                    <Link
                      to={`/works/${draft.work_id}`}
                      className="drafts-page-work-link"
                      title={`Voir l'œuvre ${draft.work_title_fr}`}
                    >
                      ← {draft.work_title_fr}
                    </Link>
                  </header>
                )}
                <DraftCard
                  draft={draft}
                  onApprove={handleApprove}
                  onRegenerate={handleRegenerate}
                  onReject={handleReject}
                  onReopen={handleReopen}
                  isBusy={busyDraftId === draft.id}
                />
              </article>
            ))}
          </div>
          {hasMore && (
            <div className="drafts-page-load-more">
              <Button
                variant="ghost"
                onClick={() => setFilter('offset', (offset + PAGE_SIZE) as typeof filters.offset)}
                loading={draftsQuery.isFetching}
              >
                Charger {Math.min(PAGE_SIZE, total - items.length)} de plus
              </Button>
              <span className="ui-caption drafts-page-load-summary">
                {items.length} sur {total}
              </span>
            </div>
          )}
        </>
      )}

      {/* ── Flow approve PR2 — 3 écrans chaînés ──────────────────────────
          1. <DraftApproveDialog> : variante (si plusieurs) + tags suggérés.
             Le picker de variantes attend que `useWork(draft.work_id)` se
             résolve — les rows variantes sont jointes côté `WorkResponse`.
          2. <MockupTemplatePicker> : 1..8 mockups.
          3. <DraftPreviewModal> : previews + bouton Confirmer & publier.
       */}
      {flowState?.step === 'prep' ? (
        <DraftApproveDialog
          isOpen
          onClose={() => setFlowState(null)}
          draftKindLabel={DRAFT_KIND_FR[flowState.draft.kind]}
          activeVariants={flowCompatibleVariants}
          suggestedTags={flowState.draft.current_version?.suggested_tags ?? []}
          onConfirm={handlePrepConfirm}
        />
      ) : null}

      {flowState?.step === 'mockups' && flowWorkQuery.data ? (
        <MockupTemplatePicker
          workId={flowState.draft.work_id}
          workTitle={flowWorkQuery.data.title_fr ?? ''}
          formatSlug={null}
          isOpen
          onClose={() => setFlowState(null)}
          onConfirm={handleMockupsConfirm}
          isSubmitting={approveMutation.isPending}
          confirmLabel="Approuver et rendre"
          submittingLabel="Render…"
          kicker={`Approbation ${DRAFT_KIND_FR[flowState.draft.kind]}`}
          title="Choisir les mockups (1 à 8)"
        />
      ) : null}

      {flowState?.step === 'preview' ? (
        <DraftPreviewModal
          isOpen
          onClose={() => setFlowState(null)}
          draftKindLabel={DRAFT_KIND_FR[flowState.draft.kind]}
          previews={flowState.previews}
          onConfirmPublish={handleConfirmPublish}
          isPublishing={enqueuePublishMutation.isPending}
        />
      ) : null}
    </Page>
  )
}
